import { useState } from 'react';
import { X, CloudUpload } from 'lucide-react';

interface CreateLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (labelData: any) => void;
}

interface LabelFormData {
  name: string;
  companyName: string;
  address: string;
  city: string;
  state: string;
  countryOrigin: string;
  phone: string;
  email: string;
  mktType: string;
  recordType: string;
  logo: File | null;
}

export default function CreateLabelModal({ isOpen, onClose, onSubmit }: CreateLabelModalProps) {
  const [formData, setFormData] = useState<LabelFormData>({
    name: '',
    companyName: '',
    address: '',
    city: '',
    state: '',
    countryOrigin: '',
    phone: '',
    email: '',
    mktType: '',
    recordType: '',
    logo: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof LabelFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        name: '',
        companyName: '',
        address: '',
        city: '',
        state: '',
        countryOrigin: '',
        phone: '',
        email: '',
        mktType: '',
        recordType: '',
        logo: null
      });
    } catch (error) {
      console.error('Error creating label:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '980px',
          height: '100vh',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header */}
        <div className="bg-secondary-500 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-[40px] leading-none font-medium">Create Label</h2>
              <p className="text-[24px] text-white/80 mt-2">Fill details to create label</p>
            </div>
            <button 
              onClick={onClose}
              className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 120px)' }}>
          <form onSubmit={handleSubmit} className="p-6">
            {/* First Row - Three Columns */}
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter name"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter address"
                />
              </div>
            </div>

            {/* Second Row - Three Columns */}
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter state"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Country Origin <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.countryOrigin}
                  onChange={(e) => handleInputChange('countryOrigin', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                >
                  <option value="">Select</option>
                  <option value="us">United States</option>
                  <option value="uk">United Kingdom</option>
                  <option value="de">Germany</option>
                  <option value="ae">UAE</option>
                  <option value="iq">Iraq</option>
                </select>
              </div>
            </div>

            {/* Third Row - Three Columns */}
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                  placeholder="Enter email"
                />
              </div>

              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  Record Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.recordType}
                  onChange={(e) => handleInputChange('recordType', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                >
                  <option value="">Select</option>
                  <option value="type1">Record Type 1</option>
                  <option value="type2">Record Type 2</option>
                  <option value="type3">Record Type 3</option>
                </select>
              </div>
            </div>

            {/* Fourth Row - Two Columns */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                  MKT Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.mktType}
                  onChange={(e) => handleInputChange('mktType', e.target.value)}
                  required
                  className="h-11 w-full rounded-lg border border-[#D6DDEE] px-3 text-[14px] text-[#6A7698] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]"
                >
                  <option value="">Select</option>
                  <option value="type1">Type 1</option>
                  <option value="type2">Type 2</option>
                  <option value="type3">Type 3</option>
                </select>
              </div>

                             <div>
                 <label className="block text-[16px] font-semibold text-[#111318] mb-2">
                   Logo <span className="text-red-500">*</span>
                 </label>
                 <div className="border border-dashed border-yellow-400 bg-[#FFF7DA] rounded-lg px-3 py-2 hover:bg-[#FFF1C7] transition-colors cursor-pointer flex items-center justify-center" style={{ height: '44px' }}>
                   <input
                     type="file"
                     accept="image/*"
                     onChange={handleLogoUpload}
                     className="hidden"
                     id="logo-upload"
                     required
                   />
                   <label htmlFor="logo-upload" className="cursor-pointer flex items-center">
                     <CloudUpload className="h-6 w-6 text-yellow-500 mr-2" />
                     <span className="text-sm text-[#6A7698]">Upload Logo</span>
                   </label>
                 </div>
               </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-[#F6C600] py-3 px-6 text-[18px] font-medium text-black transition-colors hover:bg-[#e1b500] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
